import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Redirect, useHistory } from "react-router-dom";
import {
  FaPlus,
  FaSave,
  FaSchool,
  FaStar,
  FaSyncAlt,
  FaTrashAlt,
  FaLayerGroup,
  FaCheck,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/school-profile.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const LOGO_ACCEPT = "image/*";
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

const EMPTY_FORM = {
  affiliation_id: "",
  affiliation_no: "",
  affiliation_name: "",
  school_name: "",
  school_address: "",
  school_tag_line: "",
  officail_contact_no1: "",
  officail_contact_no2: "",
  officail_email: "",
  website: "",
  school_logo_path: "",
  valid_from: "",
  valid_to: "",
  is_primary: 0,
  sort_order: 0,
  remark: "",
};

// Created By Cursor
function sessionIdFromManager() {
  return SessionManager.getCurrentSession()?.[0]?.session_id || "";
}

// Created By Cursor
function withCacheBust(url) {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  return `${raw.split("?")[0]}?v=${Date.now()}`;
}

// Created By Cursor
function parseUploadApiResponse(data) {
  if (data == null) return null;
  if (typeof data === "object" && !Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

// Created By Cursor
function toFormBody(fields) {
  const body = new URLSearchParams();
  Object.keys(fields || {}).forEach((key) => {
    const val = fields[key];
    if (val === undefined || val === null) return;
    body.append(key, String(val));
  });
  return body;
}

export default function SchoolAffiliations() {
  const history = useHistory();
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [affiliations, setAffiliations] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const logoInputRef = useRef(null);
  const sessionId = useMemo(() => sessionIdFromManager(), []);

  // Created By Cursor
  const loadClasses = useCallback(async (sid) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sid) return;
    try {
      const { data } = await axios.get(`${API_BASE()}getClassWithStudentCount.php`, {
        params: { school_code: schoolCode, session_id: sid },
      });
      if (data?.api_status === "OK") {
        setAllClasses(Array.isArray(data.data?.class) ? data.data.class : []);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Created By Cursor
  const loadAffiliations = useCallback(
    async ({ soft = false, preferId } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      const sid = sessionIdFromManager();
      if (!schoolCode || !sid) {
        myToast.notify_warning("School / session missing. Please login again.");
        setLoading(false);
        return;
      }
      if (!soft) setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE()}getSchoolAffiliations.php`, {
          params: { school_code: schoolCode, session_id: sid },
        });
        if (data?.api_status === "OK") {
          const list = Array.isArray(data.data) ? data.data : [];
          setAffiliations(list);
          const nextId =
            preferId ||
            selectedId ||
            list.find((a) => Number(a.is_primary) === 1)?.affiliation_id ||
            list[0]?.affiliation_id ||
            "";
          if (nextId) {
            setSelectedId(nextId);
          } else {
            setSelectedId("");
            setForm(EMPTY_FORM);
            setSelectedClassIds([]);
          }
        } else {
          myToast.notify_warning(data?.api_remark || "Failed to load affiliations");
        }
        await loadClasses(sid);
      } catch (e) {
        myToast.notify_error("Network error loading affiliations");
      } finally {
        setLoading(false);
      }
    },
    [loadClasses, selectedId]
  );

  useEffect(() => {
    loadAffiliations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Created By Cursor — hydrate form when selection changes
  useEffect(() => {
    if (!selectedId) return;
    const schoolCode = SessionManager.getSchoolCode();
    const sid = sessionIdFromManager();
    if (!schoolCode || !sid) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE()}getSchoolAffiliation.php`, {
          params: {
            school_code: schoolCode,
            session_id: sid,
            affiliation_id: selectedId,
          },
        });
        if (cancelled) return;
        if (data?.api_status === "OK" && data.data) {
          const a = data.data;
          setForm({
            affiliation_id: a.affiliation_id || "",
            affiliation_no: a.affiliation_no || "",
            affiliation_name: a.affiliation_name || "",
            school_name: a.school_name || "",
            school_address: a.school_address || "",
            school_tag_line: a.school_tag_line || "",
            officail_contact_no1: a.officail_contact_no1 || "",
            officail_contact_no2: a.officail_contact_no2 || "",
            officail_email: a.officail_email || "",
            website: a.website || "",
            school_logo_path: a.school_logo_path || "",
            valid_from: a.valid_from || "",
            valid_to: a.valid_to || "",
            is_primary: Number(a.is_primary) === 1 ? 1 : 0,
            sort_order: Number(a.sort_order) || 0,
            remark: a.remark || "",
          });
          setLogoBroken(false);
          const mapped = Array.isArray(a.classes)
            ? a.classes.map((c) => c.class_id)
            : [];
          setSelectedClassIds(mapped);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Created By Cursor
  const handleChange = useCallback((e) => {
    const { id, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  }, []);

  // Created By Cursor
  const startCreate = useCallback(() => {
    setSelectedId("");
    setForm({ ...EMPTY_FORM, affiliation_name: "New affiliation" });
    setSelectedClassIds([]);
    setLogoBroken(false);
  }, []);

  // Created By Cursor — native label opens picker instantly
  const onLogoLabelClick = useCallback(
    (e) => {
      const aid = form.affiliation_id || selectedId;
      if (!aid) {
        e.preventDefault();
        myToast.notify_warning("Save the affiliation first, then upload its logo");
        return;
      }
      if (uploadingLogo) {
        e.preventDefault();
      }
    },
    [form.affiliation_id, selectedId, uploadingLogo]
  );

  // Created By Cursor
  const handleLogoSelected = useCallback(
    async (event) => {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      const schoolCode = SessionManager.getSchoolCode();
      const sid = sessionIdFromManager();
      const aid = form.affiliation_id || selectedId;
      if (!schoolCode || !sid || !aid) {
        myToast.notify_warning("Save the affiliation first, then upload its logo");
        return;
      }

      const nameExt = String(file.name || "")
        .split(".")
        .pop()
        .toLowerCase();
      const okExt = ["jpg", "jpeg", "png", "gif", "webp"].includes(nameExt);
      if (!okExt && !String(file.type || "").startsWith("image/")) {
        myToast.notify_warning("Please choose an image (JPG, PNG, GIF, or WEBP).");
        return;
      }
      if (file.size > LOGO_MAX_BYTES) {
        myToast.notify_warning("Logo must be 2 MB or smaller.");
        return;
      }

      const formData = new FormData();
      formData.append("image", file, file.name || `aff_logo.${nameExt || "png"}`);
      formData.append("school_code", schoolCode);
      formData.append("session_id", sid);
      formData.append("affiliation_id", aid);

      setUploadingLogo(true);
      try {
        const response = await axios.post(
          `${API_BASE()}uploadAffiliationLogo.php`,
          formData
        );
        const payload = parseUploadApiResponse(response.data);
        if (payload?.api_status === "OK") {
          const next = withCacheBust(payload.school_logo_path || "");
          setForm((prev) => ({ ...prev, school_logo_path: next }));
          setLogoBroken(false);
          myToast.notify_success(payload.api_remark || "Logo uploaded");
          await loadAffiliations({ soft: true, preferId: aid });
        } else {
          myToast.notify_warning(payload?.api_remark || "Logo upload failed");
        }
      } catch (error) {
        const errPayload = parseUploadApiResponse(error.response?.data);
        myToast.notify_warning(
          (errPayload && (errPayload.api_remark || errPayload.message)) ||
            "Logo upload failed."
        );
      } finally {
        setUploadingLogo(false);
      }
    },
    [form.affiliation_id, loadAffiliations, selectedId]
  );

  // Created By Cursor
  const handleSave = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const sid = sessionIdFromManager();
    if (!schoolCode || !sid) return;
    if (!String(form.affiliation_name || "").trim() && !String(form.school_name || "").trim()) {
      myToast.notify_warning("Enter affiliation name or school name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_BASE()}setSchoolAffiliation.php`,
        toFormBody({
          school_code: schoolCode,
          session_id: sid,
          ...form,
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (data?.api_status === "OK") {
        myToast.notify_success(data.api_remark || "Saved");
        const newId = data.affiliation_id || form.affiliation_id;
        await loadAffiliations({ soft: true, preferId: newId });
        if (newId && selectedClassIds.length) {
          await axios.post(
            `${API_BASE()}mapAffiliationClasses.php`,
            toFormBody({
              school_code: schoolCode,
              session_id: sid,
              affiliation_id: newId,
              class_ids: JSON.stringify(selectedClassIds),
            }),
            { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
          );
        }
        setSelectedId(newId);
      } else {
        myToast.notify_warning(data?.api_remark || "Save failed");
      }
    } catch (e) {
      const remark = e?.response?.data?.api_remark;
      myToast.notify_error(remark || "Network error while saving");
    } finally {
      setSaving(false);
    }
  }, [form, loadAffiliations, selectedClassIds]);

  // Created By Cursor
  const handleMapClasses = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const sid = sessionIdFromManager();
    const aid = form.affiliation_id || selectedId;
    if (!schoolCode || !sid || !aid) {
      myToast.notify_warning("Save the affiliation first, then map classes");
      return;
    }
    setMapping(true);
    try {
      const { data } = await axios.post(
        `${API_BASE()}mapAffiliationClasses.php`,
        toFormBody({
          school_code: schoolCode,
          session_id: sid,
          affiliation_id: aid,
          class_ids: JSON.stringify(selectedClassIds),
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (data?.api_status === "OK") {
        myToast.notify_success(data.api_remark || "Classes mapped");
        await loadAffiliations({ soft: true, preferId: aid });
      } else {
        myToast.notify_warning(data?.api_remark || "Mapping failed");
      }
    } catch (e) {
      const remark = e?.response?.data?.api_remark;
      myToast.notify_error(remark || "Network error while mapping classes");
    } finally {
      setMapping(false);
    }
  }, [form.affiliation_id, loadAffiliations, selectedClassIds, selectedId]);

  // Created By Cursor
  const handleSetPrimary = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const sid = sessionIdFromManager();
    const aid = form.affiliation_id || selectedId;
    if (!aid) return;
    try {
      const { data } = await axios.post(
        `${API_BASE()}setPrimaryAffiliation.php`,
        toFormBody({
          school_code: schoolCode,
          session_id: sid,
          affiliation_id: aid,
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (data?.api_status === "OK") {
        myToast.notify_success("Primary affiliation set");
        setForm((p) => ({ ...p, is_primary: 1 }));
        await loadAffiliations({ soft: true, preferId: aid });
      } else {
        myToast.notify_warning(data?.api_remark || "Failed");
      }
    } catch (e) {
      myToast.notify_error("Network error");
    }
  }, [form.affiliation_id, loadAffiliations, selectedId]);

  // Created By Cursor
  const handleDeactivate = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const sid = sessionIdFromManager();
    const aid = form.affiliation_id || selectedId;
    if (!aid) return;
    if (!window.confirm("Deactivate this affiliation? Classes will be unmapped.")) {
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_BASE()}setSchoolAffiliation.php`,
        toFormBody({
          school_code: schoolCode,
          session_id: sid,
          affiliation_id: aid,
          status: "InActive",
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (data?.api_status === "OK") {
        myToast.notify_success("Affiliation deactivated");
        setSelectedId("");
        setForm(EMPTY_FORM);
        await loadAffiliations({ soft: true, preferId: "" });
      } else {
        myToast.notify_warning(data?.api_remark || "Failed");
      }
    } catch (e) {
      myToast.notify_error("Network error");
    }
  }, [form.affiliation_id, loadAffiliations, selectedId]);

  // Created By Cursor
  const toggleClass = useCallback((classId) => {
    setSelectedClassIds((prev) =>
      prev.includes(classId)
        ? prev.filter((id) => id !== classId)
        : [...prev, classId]
    );
  }, []);

  // Created By Cursor — classes owned by other affiliations shown with badge
  const classOwnerMap = useMemo(() => {
    const map = {};
    affiliations.forEach((a) => {
      const names = String(a.class_names || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      // rebuild from allClasses affiliation_id when present
      allClasses.forEach((c) => {
        if (String(c.affiliation_id) === String(a.affiliation_id)) {
          map[c.class_id] = a;
        }
      });
      // fallback if class list lacks affiliation_id yet
      if (names.length && a.class_count) {
        /* handled via affiliation_id on classes when migrated */
      }
    });
    return map;
  }, [affiliations, allClasses]);

  if (!loggedIn) return <Redirect to="/" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page sp-page spd-page saf-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i className="fa fa-home" aria-hidden style={{ color: COLORS.white }} />
              <span style={{ color: COLORS.white }}> School Affiliations</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <span className="sp-hero__logo sp-hero__logo--fallback" aria-hidden>
              <FaSchool />
            </span>
            <div>
              <h1 className="sm-hero__title">Manage Multi Affiliation</h1>
              {sessionId ? (
                <p className="sm-hero__sub">Session {sessionId}</p>
              ) : null}
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="pp-icon-btn"
              onClick={() => loadAffiliations({ soft: true })}
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="saf-layout">
          <aside className="saf-list">
            <button type="button" className="sp-btn sp-btn--primary saf-add" onClick={startCreate}>
              <FaPlus aria-hidden /> New affiliation
            </button>
            {affiliations.length === 0 ? (
              <div className="sp-empty">No affiliations yet. Create one to get started.</div>
            ) : (
              affiliations.map((a) => (
                <button
                  key={a.affiliation_id}
                  type="button"
                  className={`saf-list__item${
                    selectedId === a.affiliation_id ? " is-active" : ""
                  }`}
                  onClick={() => setSelectedId(a.affiliation_id)}
                >
                  <span className="saf-list__row">
                    {a.school_logo_path ? (
                      <img
                        src={a.school_logo_path}
                        alt=""
                        className="saf-list__logo"
                      />
                    ) : (
                      <span className="saf-list__logo saf-list__logo--fallback" aria-hidden>
                        <FaSchool />
                      </span>
                    )}
                    <span className="saf-list__copy">
                      <span className="saf-list__title">
                        {a.affiliation_name || a.school_name || a.affiliation_id}
                        {Number(a.is_primary) === 1 ? (
                          <FaStar className="saf-star" title="Primary" />
                        ) : null}
                      </span>
                      <span className="saf-list__meta">
                        {a.affiliation_no || "No affiliation no."} · {a.class_count || 0}{" "}
                        classes
                      </span>
                      {a.class_names ? (
                        <span className="saf-list__classes">{a.class_names}</span>
                      ) : null}
                    </span>
                  </span>
                </button>
              ))
            )}
          </aside>

          <div className="saf-main">
            <section className="spd-card">
              <h2 className="spd-card__title">
                {form.affiliation_id ? "Edit affiliation" : "Create affiliation"}
              </h2>

              <div className="saf-logo-block">
                <div
                  className={`saf-logo-preview${
                    uploadingLogo ? " is-uploading" : ""
                  }`}
                >
                  {form.school_logo_path && !logoBroken ? (
                    <img
                      src={form.school_logo_path}
                      alt="Affiliation logo"
                      onError={() => setLogoBroken(true)}
                    />
                  ) : (
                    <span aria-hidden>
                      <FaSchool />
                    </span>
                  )}
                </div>
                <div className="saf-logo-actions">
                  <p className="saf-logo-hint">
                    Separate logo for this affiliation (used on printables for its
                    classes).
                  </p>
                  <label
                    htmlFor="saf-aff-logo-file"
                    className={`sp-btn sp-btn--ghost saf-logo-btn${
                      uploadingLogo ? " is-busy" : ""
                    }`}
                    onClick={onLogoLabelClick}
                    aria-disabled={uploadingLogo}
                  >
                    {uploadingLogo
                      ? "Uploading…"
                      : form.school_logo_path
                        ? "Change logo"
                        : "Upload logo"}
                  </label>
                  {!(form.affiliation_id || selectedId) ? (
                    <span className="saf-logo-note">Save affiliation first to upload</span>
                  ) : null}
                  <input
                    id="saf-aff-logo-file"
                    ref={logoInputRef}
                    type="file"
                    accept={LOGO_ACCEPT}
                    onChange={handleLogoSelected}
                    disabled={uploadingLogo}
                    tabIndex={-1}
                  />
                </div>
              </div>

              <div className="spd-grid">
                <label className="spd-field">
                  <span className="spd-field__label">Affiliation name</span>
                  <input
                    id="affiliation_name"
                    className="spd-field__input"
                    value={form.affiliation_name}
                    onChange={handleChange}
                    placeholder="e.g. Primary Wing"
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Affiliation no.</span>
                  <input
                    id="affiliation_no"
                    className="spd-field__input"
                    value={form.affiliation_no}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field spd-field--full">
                  <span className="spd-field__label">School name (on this affiliation)</span>
                  <input
                    id="school_name"
                    className="spd-field__input"
                    value={form.school_name}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field spd-field--full">
                  <span className="spd-field__label">Address</span>
                  <textarea
                    id="school_address"
                    className="spd-field__input spd-field__input--area"
                    value={form.school_address}
                    onChange={handleChange}
                    rows={2}
                  />
                </label>
                <label className="spd-field spd-field--full">
                  <span className="spd-field__label">Tag line</span>
                  <input
                    id="school_tag_line"
                    className="spd-field__input"
                    value={form.school_tag_line}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Contact 1</span>
                  <input
                    id="officail_contact_no1"
                    className="spd-field__input"
                    value={form.officail_contact_no1}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Contact 2</span>
                  <input
                    id="officail_contact_no2"
                    className="spd-field__input"
                    value={form.officail_contact_no2}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Email</span>
                  <input
                    id="officail_email"
                    type="email"
                    className="spd-field__input"
                    value={form.officail_email}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Website</span>
                  <input
                    id="website"
                    className="spd-field__input"
                    value={form.website}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Valid from</span>
                  <input
                    id="valid_from"
                    type="date"
                    className="spd-field__input"
                    value={form.valid_from || ""}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field">
                  <span className="spd-field__label">Valid to</span>
                  <input
                    id="valid_to"
                    type="date"
                    className="spd-field__input"
                    value={form.valid_to || ""}
                    onChange={handleChange}
                  />
                </label>
                <label className="spd-field saf-check">
                  <input
                    id="is_primary"
                    type="checkbox"
                    checked={Number(form.is_primary) === 1}
                    onChange={handleChange}
                  />
                  <span>Primary affiliation (default when no class context)</span>
                </label>
              </div>

              <div className="sp-actions spd-actions">
                <button
                  type="button"
                  className="sp-btn sp-btn--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <FaSave aria-hidden />
                  {saving ? "Saving…" : "Save affiliation"}
                </button>
                {(form.affiliation_id || selectedId) && Number(form.is_primary) !== 1 ? (
                  <button
                    type="button"
                    className="sp-btn sp-btn--ghost"
                    onClick={handleSetPrimary}
                  >
                    <FaStar aria-hidden /> Set primary
                  </button>
                ) : null}
                {form.affiliation_id || selectedId ? (
                  <button
                    type="button"
                    className="sp-btn sp-btn--ghost"
                    onClick={handleDeactivate}
                  >
                    <FaTrashAlt aria-hidden /> Deactivate
                  </button>
                ) : null}
                <button
                  type="button"
                  className="sp-btn sp-btn--ghost"
                  onClick={() => history.push("/AdminMain/SchoolProfile")}
                >
                  Back to profile
                </button>
              </div>
            </section>

            <section className="spd-card saf-map">
              <header className="saf-map__head">
                <div className="saf-map__head-text">
                  <span className="saf-map__icon" aria-hidden>
                    <FaLayerGroup />
                  </span>
                  <div>
                    <h2 className="spd-card__title saf-map__title">Map classes</h2>
                    <p className="saf-hint saf-map__hint">
                      Assign class ranges (e.g. Play–V, VI–VIII, IX–XII). Board and
                      medium stay on each class.
                    </p>
                  </div>
                </div>
                <span className="saf-map__count">
                  {selectedClassIds.length} selected
                </span>
              </header>

              {allClasses.length > 0 ? (
                <div className="saf-map__toolbar">
                  <button
                    type="button"
                    className="saf-map__tool"
                    onClick={() =>
                      setSelectedClassIds(allClasses.map((c) => c.class_id))
                    }
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="saf-map__tool"
                    onClick={() => setSelectedClassIds([])}
                    disabled={selectedClassIds.length === 0}
                  >
                    Clear
                  </button>
                </div>
              ) : null}

              {allClasses.length === 0 ? (
                <div className="sp-empty">No classes in this session.</div>
              ) : (
                <div className="saf-class-grid">
                  {allClasses.map((c) => {
                    const owner = classOwnerMap[c.class_id];
                    const checked = selectedClassIds.includes(c.class_id);
                    const otherOwner =
                      owner &&
                      owner.affiliation_id &&
                      owner.affiliation_id !== (form.affiliation_id || selectedId);
                    const board = c.board_name || c.board_id;
                    const medium = c.medium_name || c.medium_id;
                    return (
                      <label
                        key={c.class_id}
                        className={`saf-class${checked ? " is-checked" : ""}${
                          otherOwner ? " has-owner" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="saf-class__input"
                          checked={checked}
                          onChange={() => toggleClass(c.class_id)}
                        />
                        <span className="saf-class__tick" aria-hidden>
                          {checked ? <FaCheck /> : null}
                        </span>
                        <span className="saf-class__body">
                          <span className="saf-class__name">{c.class_name}</span>
                          <span className="saf-class__chips">
                            {board ? (
                              <span className="saf-class__chip">{board}</span>
                            ) : null}
                            {medium ? (
                              <span className="saf-class__chip saf-class__chip--soft">
                                {medium}
                              </span>
                            ) : null}
                          </span>
                          {otherOwner ? (
                            <span className="saf-class__owner">
                              Now:{" "}
                              {otherOwner.affiliation_name ||
                                otherOwner.affiliation_id}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="sp-actions spd-actions saf-map__actions">
                <button
                  type="button"
                  className="sp-btn sp-btn--primary"
                  onClick={handleMapClasses}
                  disabled={mapping || !(form.affiliation_id || selectedId)}
                >
                  {mapping ? "Mapping…" : "Save class mapping"}
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
